import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Clock, Building2, Briefcase, Store, Check, Zap, Mountain, Users, Wrench, Heart } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { SEO } from '../../components/SEO';

export default function HauteSavoiePage() {
  return (
    <main data-testid="seo-page-haute-savoie" className="pt-32">
      <SEO 
        title="Création de site web en Haute-Savoie (74) | Annecy, Chamonix, Praz-sur-Arly, Megève"
        description="Webdesigner freelance en Haute-Savoie. Création de sites web pour commerces, PME et indépendants à Annecy, Chamonix, Praz-sur-Arly, Megève, Sallanches. Devis gratuit sous 48h."
        path="/creation-site-web-haute-savoie"
      />

      {/* Hero Section */}
      <section className="container-main mb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <div className="flex items-center gap-3 mb-4">
              <img 
                src="/images/seo/savoie.png" 
                alt="Haute-Savoie" 
                className="w-10 h-10 object-contain"
              />
              <span className="text-sm uppercase tracking-widest font-bold text-primary">
                Département de la Haute-Savoie (74)
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight mb-6">
              Création de site web
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-blue-700 pb-2">
                en Haute-Savoie
              </span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8">
              Webdesigner freelance avec de la famille dans le Val d'Arly, je connais bien la Haute-Savoie. De <strong>Praz-sur-Arly</strong> à <strong>Annecy</strong>, j'accompagne les commerces, PME et indépendants du département.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/tarifs#contact">
                <Button className="bg-gradient-to-r from-cyan-600 to-blue-700 hover:from-cyan-700 hover:to-blue-800 text-white text-lg px-8 py-6">
                  Discuter de mon projet
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/tarifs">
                <Button variant="outline" className="text-lg px-8 py-6">
                  Voir les tarifs
                </Button>
              </Link>
            </div>
          </div>

          {/* Image Mont-Blanc */}
          <div className="animate-fade-up animation-delay-200">
            <img 
              src="/images/seo/mont-blanc.jpg"
              alt="Création site web Haute-Savoie - Vue du Mont-Blanc"
              className="w-full h-auto rounded-2xl object-cover shadow-2xl"
            />
          </div>
        </div>
      </section>

      {/* Section: Un territoire que je connais */}
      <section className="bg-muted/30 section-padding">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Stats Card */}
            <div className="order-2 lg:order-1">
              <Card className="overflow-hidden border-2 bg-gradient-to-br from-cyan-600 to-blue-700 text-white">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-6">La Haute-Savoie en chiffres</h3>
                  <div className="grid grid-cols-2 gap-6">
                    <div className="text-center p-4 bg-white/10 rounded-xl">
                      <div className="text-3xl font-black">820 000</div>
                      <div className="text-sm opacity-80">habitants</div>
                    </div>
                    <div className="text-center p-4 bg-white/10 rounded-xl">
                      <div className="text-3xl font-black">279</div>
                      <div className="text-sm opacity-80">communes</div>
                    </div>
                    <div className="text-center p-4 bg-white/10 rounded-xl">
                      <div className="text-3xl font-black">4 810m</div>
                      <div className="text-sm opacity-80">Mont-Blanc</div>
                    </div>
                    <div className="text-center p-4 bg-white/10 rounded-xl">
                      <div className="text-3xl font-black">1er</div>
                      <div className="text-sm opacity-80">département ski FR</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 bg-cyan-100 dark:bg-cyan-950/50 text-cyan-700 dark:text-cyan-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Heart className="h-4 w-4" />
                Un territoire que je connais
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Webdesigner freelance<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-blue-700">
                  du lac d'Annecy au Mont-Blanc
                </span>
              </h2>
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                J'ai de la famille dans le Val d'Arly, à <strong>Praz-sur-Arly</strong>. Je connais ces montagnes depuis tout petit. 
                La Haute-Savoie, c'est <strong>820 000 habitants</strong>, un territoire dynamique entre lacs et sommets.
              </p>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                Que vous soyez à <strong>Annecy</strong>, <strong>Chamonix</strong>, <strong>Megève</strong> ou dans une station du Val d'Arly, 
                je me déplace pour vous rencontrer. Création de site web sur-mesure pour votre activité.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-4 bg-white dark:bg-card rounded-xl border">
                  <Heart className="h-8 w-8 text-cyan-600 shrink-0" />
                  <div>
                    <div className="font-bold">Attaché</div>
                    <div className="text-sm text-muted-foreground">Famille sur place</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-white dark:bg-card rounded-xl border">
                  <MapPin className="h-8 w-8 text-cyan-600 shrink-0" />
                  <div>
                    <div className="font-bold">Mobile</div>
                    <div className="text-sm text-muted-foreground">Je me déplace</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-white dark:bg-card rounded-xl border">
                  <Zap className="h-8 w-8 text-cyan-600 shrink-0" />
                  <div>
                    <div className="font-bold">Réactif</div>
                    <div className="text-sm text-muted-foreground">Réponse sous 48h</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-white dark:bg-card rounded-xl border">
                  <Wrench className="h-8 w-8 text-cyan-600 shrink-0" />
                  <div>
                    <div className="font-bold">Sur-mesure</div>
                    <div className="text-sm text-muted-foreground">Adapté à vos besoins</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section: Pour qui */}
      <section className="section-padding">
        <div className="container-main">
          <div className="text-center mb-12">
            <span className="text-sm uppercase tracking-widest font-bold text-primary mb-4 block">
              Pour qui ?
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Création de site web pour les professionnels<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-blue-700">
                de Haute-Savoie
              </span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Commerces, artisans, PME, indépendants à Annecy, Chamonix, Praz-sur-Arly, Megève... Un site web adapté à votre activité.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Card: Commerces locaux */}
            <Card className="group hover:border-cyan-500/50 transition-all hover:shadow-lg border-2">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform">
                  <Store className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold mb-3">Commerces & Artisans</h3>
                <p className="text-muted-foreground mb-4">
                  Restaurants, boutiques, artisans... Une vitrine en ligne pour attirer clientèle locale et touristes.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Fiche Google optimisée
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Photos & galerie
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Réservation en ligne
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Card: PME */}
            <Card className="group hover:border-cyan-500/50 transition-all hover:shadow-lg border-2">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform">
                  <Building2 className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold mb-3">PME</h3>
                <p className="text-muted-foreground mb-4">
                  Entreprises de services, BTP, industrie... Un site vitrine professionnel pour valoriser votre expertise.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Présentation métiers
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Références clients
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Formulaire de contact pro
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Card: Indépendants */}
            <Card className="group hover:border-cyan-500/50 transition-all hover:shadow-lg border-2">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform">
                  <Briefcase className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold mb-3">Indépendants</h3>
                <p className="text-muted-foreground mb-4">
                  Consultants, coachs, professions libérales... Un site personnel qui génère des contacts qualifiés.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Personal branding
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Prise de RDV en ligne
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Optimisé SEO local
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Section: Villes couvertes */}
      <section className="bg-gradient-to-br from-slate-900 to-slate-800 text-white section-padding">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Création de site web dans toute la Haute-Savoie (74)
            </h2>
            <p className="text-slate-300 max-w-2xl mx-auto">
              Annecy, Chamonix, Praz-sur-Arly, Megève, Thonon-les-Bains... Je me déplace pour vous rencontrer.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { name: 'Annecy', desc: 'Préfecture, lac', Icon: MapPin },
              { name: 'Chamonix', desc: 'Mont-Blanc', Icon: Mountain },
              { name: 'Thonon-les-Bains', desc: 'Lac Léman', Icon: MapPin },
              { name: 'Sallanches', desc: 'Vallée de l\'Arve', Icon: Building2 },
              { name: 'Megève', desc: 'Station prestige', Icon: Mountain },
              { name: 'Praz-sur-Arly', desc: 'Val d\'Arly', Icon: Heart },
              { name: 'Cluses', desc: 'Décolletage', Icon: Building2 },
              { name: 'Annemasse', desc: 'Agglomération', Icon: Building2 },
            ].map((zone) => (
              <div key={zone.name} className="text-center p-5 bg-white/5 rounded-xl hover:bg-white/10 transition-colors">
                <div className="w-11 h-11 mx-auto rounded-full bg-gradient-to-br from-cyan-600 to-blue-700 flex items-center justify-center mb-3">
                  <zone.Icon className="h-5 w-5 text-white" />
                </div>
                <h3 className="font-bold text-white text-sm">{zone.name}</h3>
                <p className="text-xs text-slate-400 mt-1">{zone.desc}</p>
              </div>
            ))}
          </div>

          {/* Stats */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-6 bg-white/5 rounded-xl">
              <div className="text-3xl font-black text-cyan-400">820 000</div>
              <div className="text-sm text-slate-400">habitants</div>
            </div>
            <div className="text-center p-6 bg-white/5 rounded-xl">
              <div className="text-3xl font-black text-cyan-400">279</div>
              <div className="text-sm text-slate-400">communes</div>
            </div>
            <div className="text-center p-6 bg-white/5 rounded-xl">
              <div className="text-3xl font-black text-cyan-400">2h</div>
              <div className="text-sm text-slate-400">depuis l'Ain</div>
            </div>
            <div className="text-center p-6 bg-white/5 rounded-xl">
              <div className="text-3xl font-black text-cyan-400">Forfait</div>
              <div className="text-sm text-slate-400">Devis sous 48h</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="section-padding bg-muted/30">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Votre site web en Haute-Savoie
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Création de site internet à Annecy, Chamonix, Praz-sur-Arly, Megève ou ailleurs en Haute-Savoie. Devis gratuit sous 48h.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link to="/tarifs#contact">
                <Button className="bg-gradient-to-r from-cyan-600 to-blue-700 hover:from-cyan-700 hover:to-blue-800 text-white text-lg px-8 py-6 w-full sm:w-auto">
                  Demander un devis gratuit
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/processus">
                <Button variant="outline" className="text-lg px-8 py-6 w-full sm:w-auto">
                  Découvrir mon processus
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>

            <div className="flex items-center justify-center gap-6 text-muted-foreground">
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                <span>Haute-Savoie (74)</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                <span>Devis sous 48h</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
